import { CustomerService, IApplicationFilter, PortfolioReportCache, WidgetPropertyType, enumToExtendedArray, getChartReadyPromise, property, widget } from '@dynamik/common';
import { color, create, percent, unuseTheme, useTheme } from '@amcharts/amcharts4/core';
import { PieChart, PieSeries, XYChart, ColumnSeries, CategoryAxis, ValueAxis } from '@amcharts/amcharts4/charts';
import { SliceGrouper } from '@amcharts/amcharts4/plugins/sliceGrouper';
import { InstrumentValueGroup } from '@dynamik/common/clients/reporting-clients';
import { BindingEngine } from 'aurelia-binding';
import { ChartWidgetBase } from '@dynamik/common';
import { customElement } from 'aurelia-templating';

export enum PortfolioOverviewChartType {
  Pie = 1,
  Bar = 2
}

export enum PortfolioOverviewChartGroupBy {
  Type = 2,
  Category = 3
}

@customElement('portfolio-overview-chart-widget')
@widget('Portfolio overview chart', true, true, 'Description')
export class PortfolioOverviewChartWidget extends ChartWidgetBase {
  filter: IApplicationFilter;
  constructor(element: Element, customerService: CustomerService, bindingEngine: BindingEngine, private portfolioReportCache: PortfolioReportCache) {
    super(element, bindingEngine, customerService);
  }

  chartObject: PieChart | XYChart;
  grouper: SliceGrouper;

  @property('Data', 'Type', WidgetPropertyType.List, enumToExtendedArray(PortfolioOverviewChartType))
  chartType: PortfolioOverviewChartType = PortfolioOverviewChartType.Pie;

  @property('Data')
  radius?: number = 60;

  @property('Data')
  otherThreshold: number = 5;

  @property('Data', undefined, WidgetPropertyType.List, enumToExtendedArray(PortfolioOverviewChartGroupBy))
  groupBy: PortfolioOverviewChartGroupBy = PortfolioOverviewChartGroupBy.Type;

  @property('Data', 'Category type', WidgetPropertyType.List)
  categoryTypeId: number | undefined;
  async categoryTypeIdListValues() {
    return this.customerService.instrumentCategories.filter(x => x.enabled).map(x => ({ value: x.catTypeId, label: x.catTypeName }));
  }

  @property('Style')
  chartTheme: string;

  @property('Style')
  labelColour?: string;

  override async loadData(filter: IApplicationFilter) {
    this.filter = filter
    return this.portfolioReportCache.instrumentValueGroupsOverTime(filter.customer.companyId, this.groupBy === PortfolioOverviewChartGroupBy.Category ? this.categoryTypeId : undefined,
      filter.dates.fromStr, filter.dates.toStr, false);
  }

  override async setData(data: InstrumentValueGroup[]) {
     this.chartObject?.dispose();
     switch (this.chartType) {
       case PortfolioOverviewChartType.Pie:
         await this.createPieChart();
         break;
       case PortfolioOverviewChartType.Bar:
         await this.createColumnChart();
         break;
     }
     const chartData = data.filter(x => x.date >= this.filter.dates.toStr).sort((a, b) => b.estimatedValue - a.estimatedValue);
     this.chartObject.data = chartData;
     this.excelData = {
       fileName: 'Portfolio Overview Chart',
       data: { Items: chartData }
     };
  }

  async createPieChart() {
    const theme = this.customerService.getChartTheme(this.chartTheme);
    useTheme(theme);
    this.chartObject?.dispose();
    this.chartObject = create(this.chart, PieChart);
    const readyPromise = getChartReadyPromise(this.chartObject);
    this.chartObject.radius = percent(this.radius ?? 60);
    this.chartObject.innerRadius = percent(0);
    this.chartObject.hiddenState.properties.opacity = 0;
    const pieSeries = this.chartObject.series.push(new PieSeries());
    pieSeries.slices.template.cornerRadius = 6;
    pieSeries.dataFields.value = "estimatedValue";
    pieSeries.dataFields.category = "group";
    if (this.otherThreshold !== undefined) {
      this.grouper = pieSeries.plugins.push(new SliceGrouper());
      this.grouper.target = pieSeries;
      this.grouper.threshold = this.otherThreshold;
    }

    pieSeries.slices.template.stroke = color('#343a40');
    pieSeries.slices.template.cornerRadius = 8;
    pieSeries.slices.template.strokeWidth = 2;
    pieSeries.slices.template.strokeOpacity = 1;

    pieSeries.numberFormatter.numberFormat = '#,###.';
    const labelColour = color(this.getChartLabelColour(this.labelColour));
    pieSeries.ticks.template.stroke = labelColour;
    pieSeries.labels.template.fill = labelColour;
    pieSeries.alignLabels = false;
    pieSeries.labels.template.fontSize = 14;
    pieSeries.labels.template.maxWidth = 100;
    pieSeries.labels.template.wrap = true;
    pieSeries.labels.template.draggable = true;

    pieSeries.tooltip.autoTextColor = false
    pieSeries.tooltip.label.fill = color("#f2eded");
    pieSeries.slices.template.tooltipHTML = `<b>{category}:</B>  {value.percent}%<br>
                          <b>Estimated Value:</b>  {estimatedValue}<br>
                          <b>Unrealized Profit/Loss:</b>  {unrealizedProfitLoss}<br>
                          <b>Realized Profit/Loss:</b>  {realizedProfitLoss}<br>
                          <b>Interest:</b>  {interest}<br>
                          <b>Dividend:</b>  {dividend}`;
    // pieSeries.slices.template.fontSize = 20
    // pieSeries.slices.template.fontFamily ="Arial, sans-serif"; // Set font family
    this.chartObject.responsive.enabled = true;
    unuseTheme(theme);
    await readyPromise;
  }

  async createColumnChart() {
    const theme = this.customerService.getChartTheme(this.chartTheme);
    useTheme(theme);
    this.chartObject?.dispose();
    this.chartObject = create(this.chart, XYChart);
    const readyPromise = getChartReadyPromise(this.chartObject);
    this.chartObject.height = percent(100);
    this.chartObject.hiddenState.properties.opacity = 0;
    this.chartObject.fontSize = 9;

    const categoryAxis = this.chartObject.xAxes.push(new CategoryAxis());
    categoryAxis.dataFields.category = 'category';
    categoryAxis.renderer.labels.template.wrap = true;
    categoryAxis.renderer.labels.template.rotation = -90;
    const labelColour = color(this.getChartLabelColour(this.labelColour));
    categoryAxis.renderer.labels.template.fill = labelColour;
    categoryAxis.renderer.minGridDistance = 30;
    categoryAxis.renderer.grid.template.location = 0.5;
    categoryAxis.renderer.labels.template.horizontalCenter = 'right';
    categoryAxis.renderer.labels.template.verticalCenter = 'middle';

    const yAxis = new ValueAxis();
    yAxis.numberFormatter.numberFormat = '#a';
    this.chartObject.yAxes.push(yAxis);
    yAxis.renderer.labels.template.fill = labelColour;
    const colSeries = this.chartObject.series.push(new ColumnSeries());
    colSeries.dataFields.valueY = 'value';
    colSeries.dataFields.categoryX = 'category';

    colSeries.columns.template.stroke = color('#343a40');
    colSeries.columns.template.strokeWidth = 2;
    colSeries.columns.template.strokeOpacity = 1;

    colSeries.numberFormatter.numberFormat = '#,###.';
    colSeries.columns.template.tooltipText = '{category} {valueY}';
    this.chartObject.responsive.enabled = true;
    // Add distinctive colors for each column using adapter
    colSeries.columns.template.adapter.add('fill', (fill, target) => {
      return this.chartObject.colors.getIndex(target.dataItem.index);
    });
    unuseTheme(theme);
    await readyPromise;
  }

  override configChanged(key: keyof PortfolioOverviewChartWidget) {
    switch (key) {
      case 'radius':
        (this.chartObject as PieChart).radius = percent(this.radius ?? 60);
        break;
      case 'otherThreshold':
        this.grouper.threshold = this.otherThreshold;
        this.refresh$.next();
        break;
      case 'labelColour':
        const labelColour = color(this.getChartLabelColour(this.labelColour));
        if (this.chartObject instanceof XYChart) {
          this.chartObject.xAxes.each(x => x.renderer.labels.template.fill = x.renderer.ticks.template.stroke = labelColour);
          this.chartObject.yAxes.each(x => x.renderer.labels.template.fill = x.renderer.ticks.template.stroke = labelColour);
        } else {
          this.chartObject.series.each(x => x.labels.template.fill = x.ticks.template.stroke = labelColour);
        }
        break;
      default:
        this.refresh$.next();
        break;
    }
  }

}
